import React from 'react';
import {
  Alert,
  Box,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import BudgetAllocationActivityGrid from './BudgetAllocationActivityGrid';
import BudgetAllocationBulkDialog from './BudgetAllocationBulkDialog';
import BudgetAllocationFilterBar from './BudgetAllocationFilterBar';
import BudgetAllocationLineItemGrid from './BudgetAllocationLineItemGrid';
import BudgetAllocationLinksGrid from './BudgetAllocationLinksGrid';
import BudgetAllocationSummaryPanel from './BudgetAllocationSummaryPanel';

import {
  deleteLinks,
  fetchActivities,
  fetchCostCenters,
  fetchLineItems,
  fetchLinks,
  fetchSummary,
  processAllocation,
  //unmapAllocation,
} from './budgetallocation.apis';

import {
  AllocationMode,
  IAllocationActivity,
  IAllocationCostCenter,
  IAllocationLineItem,
  IAllocationLink,
} from './budgetallocation.models';

function emptySelection(): GridRowSelectionModel {
  return { type: 'include', ids: new Set() } as GridRowSelectionModel;
}

function selectionCount(selection: GridRowSelectionModel): number {
  return selection?.ids?.size ?? 0;
}

function selectionIds(selection: GridRowSelectionModel): string[] {
  return Array.from(selection?.ids ?? []).map(String);
}

type State = {
  loading: boolean;
  error: string;
  success: string;

  costcenters: IAllocationCostCenter[];
  selectedCostCenter: number | null;

  activitySearch: string;
  lineItemSearch: string;

  activities: IAllocationActivity[];
  lineitems: IAllocationLineItem[];
  links: IAllocationLink[];

  selectedActivities: GridRowSelectionModel;
  selectedLineItems: GridRowSelectionModel;
  selectedLinks: GridRowSelectionModel;

  dialogOpen: boolean;
  summary: any;
};

class BudgetAllocationWorkbench extends React.Component<any, State> {
  state: State = {
    loading: false,
    error: '',
    success: '',

    costcenters: [],
    selectedCostCenter: null,

    activitySearch: '',
    lineItemSearch: '',

    activities: [],
    lineitems: [],
    links: [],

    selectedActivities: emptySelection(),
    selectedLineItems: emptySelection(),
    selectedLinks: emptySelection(),

    dialogOpen: false,
    summary: {},
  };

  componentDidMount() {
    this.loadInitial();
  }

  loadInitial = async () => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      const [costcenters, lineitems, summary] = await Promise.all([
        fetchCostCenters(),
        fetchLineItems('', true),
        fetchSummary(),
      ]);

      const selectedCostCenter =
        Array.isArray(costcenters) && costcenters.length > 0
          ? costcenters[0].recid
          : null;

      this.setState(
        {
          costcenters: costcenters || [],
          lineitems: lineitems || [],
          selectedCostCenter,
          summary,
        },
        () => this.refreshSelectedCostCenter()
      );
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  refreshSelectedCostCenter = async () => {
    const { selectedCostCenter, activitySearch } = this.state;

    if (!selectedCostCenter) return;

    this.setState({ loading: true, error: '', success: '' });

    try {
      const [activities, links, summary] = await Promise.all([
        fetchActivities(selectedCostCenter, activitySearch),
        fetchLinks(selectedCostCenter),
        fetchSummary(selectedCostCenter),
      ]);

      this.setState({
        activities,
        links,
        summary,
        selectedActivities: emptySelection(),
        selectedLineItems: emptySelection(),
        selectedLinks: emptySelection(),
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  refreshLineItems = async () => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      const lineitems = await fetchLineItems(this.state.lineItemSearch, true);
      this.setState({ lineitems, selectedLineItems: emptySelection() });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  refreshAll = async () => {
    await Promise.all([
      this.refreshSelectedCostCenter(),
      this.refreshLineItems(),
    ]);
  };

  onCostCenterChange = (selectedCostCenter: number | null) => {
    this.setState({ selectedCostCenter }, this.refreshSelectedCostCenter);
  };

  onActivitySearchChange = (activitySearch: string) => {
    this.setState({ activitySearch }, this.refreshSelectedCostCenter);
  };

  onLineItemSearchChange = (lineItemSearch: string) => {
    this.setState({ lineItemSearch }, this.refreshLineItems);
  };

  openAllocateDialog = () => {
    this.setState({ dialogOpen: true });
  };

  closeAllocateDialog = () => {
    this.setState({ dialogOpen: false });
  };

  submitAllocation = async (mode: AllocationMode, totalAmount: number) => {
    const { selectedCostCenter, selectedActivities, selectedLineItems } = this.state;

    const activityids = selectionIds(selectedActivities);
    const lineitemids = selectionIds(selectedLineItems);

    if (!selectedCostCenter || activityids.length === 0 || lineitemids.length === 0) {
      this.setState({ error: 'Select a cost center, at least one activity and at least one line item.' });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      await processAllocation({
        costcenterid: selectedCostCenter,
        activityids,
        lineitemids,
        mode,
        totalamount: totalAmount,
      });

      this.setState({
        success: `Allocation processed for ${activityids.length} activities and ${lineitemids.length} line items.`,
        dialogOpen: false,
      });

      await this.refreshSelectedCostCenter();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  unmapSelected = async () => {
    const linkids = selectionIds(this.state.selectedLinks);

    if (linkids.length === 0) {
      this.setState({ error: 'Select one or more existing allocation links to unmap.' });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      //await unmapAllocation(linkids);
      await deleteLinks(linkids);

      this.setState({
        success: `${linkids.length} allocation link(s) unmapped.`,
      });

      await this.refreshSelectedCostCenter();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      loading,
      error,
      success,
      costcenters,
      selectedCostCenter,
      activitySearch,
      lineItemSearch,
      activities,
      lineitems,
      links,
      selectedActivities,
      selectedLineItems,
      selectedLinks,
      dialogOpen,
      summary,
    } = this.state;

    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Activity Budget Allocation Workbench
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Link programme-compliant activities to budget line items before consolidation and validation.
            </Typography>
          </Box>
        </Stack>

        <BudgetAllocationFilterBar
          costcenters={costcenters}
          selectedCostCenter={selectedCostCenter}
          activitySearch={activitySearch}
          lineItemSearch={lineItemSearch}
          selectedActivityCount={selectionCount(selectedActivities)}
          selectedLineItemCount={selectionCount(selectedLineItems)}
          selectedLinkCount={selectionCount(selectedLinks)}
          loading={loading}
          onCostCenterChange={this.onCostCenterChange}
          onActivitySearchChange={this.onActivitySearchChange}
          onLineItemSearchChange={this.onLineItemSearchChange}
          onRefresh={this.refreshAll}
          onOpenAllocate={this.openAllocateDialog}
          onUnmapSelected={this.unmapSelected}
        />

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        <Stack spacing={3}>
          <BudgetAllocationSummaryPanel summary={summary} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <BudgetAllocationActivityGrid
                rows={activities}
                selection={selectedActivities}
                loading={loading}
                onSelectionChange={(selectedActivities) => this.setState({ selectedActivities })}
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <BudgetAllocationLineItemGrid
                rows={lineitems}
                selection={selectedLineItems}
                loading={loading}
                onSelectionChange={(selectedLineItems) => this.setState({ selectedLineItems })}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <BudgetAllocationLinksGrid
                rows={links}
                selection={selectedLinks}
                loading={loading}
                onSelectionChange={(selectedLinks) => this.setState({ selectedLinks })}
              />
            </Grid>
          </Grid>
        </Stack>

        <BudgetAllocationBulkDialog
          open={dialogOpen}
          activityCount={selectionCount(selectedActivities)}
          lineItemCount={selectionCount(selectedLineItems)}
          onClose={this.closeAllocateDialog}
          onSubmit={this.submitAllocation}
        />
      </Box>
    );
  }
}

export default BudgetAllocationWorkbench;
